import { FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, Input } from '@bruno/ui';
import type { PublicCaptchaConfigDTO, TwoFactorChallengeResponse } from '@bruno/shared';
import { isTwoFactorChallenge } from '@bruno/shared';
import { useAuth } from '../auth/AuthContext';
import { useCapCaptcha } from '../auth/useCapCaptcha';
import { getCaptchaConfig, resendVerification } from '../api/auth';
import { twoFactorLogin } from '../api/account';
import { CaptchaStatusBadge } from '../components/CaptchaStatusBadge';
import { ApiError } from '../api/client';

export function LoginPage() {
  const { user, login, setSignedInUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [captchaCfg, setCaptchaCfg] = useState<PublicCaptchaConfigDTO | null>(null);
  /** Set when /v1/auth/login returns a 2FA challenge. We then swap the
   *  form into "enter your authenticator code" mode. */
  const [challenge, setChallenge] = useState<TwoFactorChallengeResponse | null>(null);
  /** True after a login attempt rejected with `email_not_verified`, so we
   *  can offer a one-click resend of the confirmation email. */
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  // Resolve captcha config exactly once on mount; failure is non-fatal —
  // we just behave as if captcha is disabled (server still enforces).
  useEffect(() => {
    let cancelled = false;
    void getCaptchaConfig()
      .then((cfg) => {
        if (!cancelled) setCaptchaCfg(cfg);
      })
      .catch(() => {
        if (!cancelled) setCaptchaCfg({ enabled: false, instanceUrl: '', siteKey: '' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const captcha = useCapCaptcha(captchaCfg ?? undefined);

  if (user) {
    const to = (location.state as { from?: string })?.from ?? '/websites';
    return <Navigate to={to} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNeedsVerification(false);
    setResendState('idle');
    setLoading(true);
    try {
      const token = captchaCfg?.enabled ? await captcha.getToken() : undefined;
      if (captchaCfg?.enabled && !token) {
        throw new Error('Captcha verification failed. Please try again.');
      }
      const res = await login(email, password, token ?? undefined);
      if (isTwoFactorChallenge(res)) {
        // Captcha tokens are single-use — drop the current one so the
        // hook resolves a fresh one if the user falls back to the
        // start of the flow (e.g. by hitting Back).
        if (captchaCfg?.enabled) captcha.reset();
        setChallenge(res);
        return;
      }
      navigate('/websites');
    } catch (err) {
      // Cap tokens are single-use — any failure means we need a fresh one
      // for the retry. Reset and let the hook auto-resolve a new token.
      if (captchaCfg?.enabled) captcha.reset();
      if (err instanceof ApiError && err.code === 'email_not_verified') {
        setNeedsVerification(true);
        setError('Please confirm your email address before signing in.');
      } else {
        setError(err instanceof Error ? err.message : 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  }

  async function onResend() {
    setResendState('sending');
    try {
      // The failed login already consumed its captcha token; the catch in
      // onSubmit reset the hook so a fresh token is pre-solved by now.
      const token = captchaCfg?.enabled ? await captcha.getToken() : undefined;
      if (captchaCfg?.enabled && !token) throw new Error('captcha unavailable');
      await resendVerification(email, token ?? undefined);
      setResendState('sent');
    } catch {
      // Spent/failed captcha or a network error — drop the token, pre-solve
      // a fresh one, and let the user retry rather than claim a false send.
      if (captchaCfg?.enabled) captcha.reset();
      setResendState('error');
    }
  }

  if (challenge) {
    return (
      <TwoFactorChallengeForm
        challenge={challenge}
        captchaCfg={captchaCfg}
        onCancel={() => {
          setChallenge(null);
          setError(null);
        }}
        onSuccess={(u) => {
          setSignedInUser(u);
          navigate('/websites');
        }}
      />
    );
  }

  return (
    <div className="min-h-screen grid place-items-center px-6">
      <Card className="w-full max-w-md">
        <div className="mb-6 flex items-center gap-3">
          <img src="/bruno-logo.svg" alt="Bruno" className="h-10 w-10" />
          <div>
            <h1 className="text-xl font-semibold">Welcome to Bruno</h1>
            <p className="text-sm text-slate-500">Sign in to your dashboard</p>
          </div>
        </div>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <div className="text-right -mt-2">
            <Link to="/forgot-password" className="text-sm text-indigo-600 hover:underline">
              Forgot password?
            </Link>
          </div>
          {error ? <div className="text-sm text-red-600">{error}</div> : null}
          {needsVerification ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {resendState === 'sent' ? (
                <span>Confirmation email sent. Check your inbox (and spam).</span>
              ) : (
                <span>
                  {resendState === 'error'
                    ? 'Could not resend just now. '
                    : "Didn't get the confirmation email? "}
                  <button
                    type="button"
                    className="font-semibold underline disabled:opacity-60"
                    onClick={onResend}
                    disabled={resendState === 'sending'}
                  >
                    {resendState === 'sending' ? 'Sending…' : 'Resend it'}
                  </button>
                  .
                </span>
              )}
            </div>
          ) : null}
          <Button type="submit" loading={loading}>
            Sign in
          </Button>
          {captchaCfg?.enabled ? <CaptchaStatusBadge state={captcha.state} /> : null}
        </form>
        <div className="mt-4 text-center text-sm text-slate-500">
          No account?{' '}
          <Link to="/signup" className="text-indigo-600 hover:underline">
            Sign up
          </Link>
        </div>
      </Card>
    </div>
  );
}

function TwoFactorChallengeForm({
  challenge,
  captchaCfg,
  onCancel,
  onSuccess,
}: {
  challenge: TwoFactorChallengeResponse;
  captchaCfg: PublicCaptchaConfigDTO | null;
  onCancel: () => void;
  onSuccess: (user: import('@bruno/shared').UserDTO) => void;
}) {
  const [mode, setMode] = useState<'totp' | 'recovery'>('totp');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Resolve a fresh Cap token for the challenge step. Cap tokens are
  // single-use, so the one acquired at the password step is already
  // burned by the time we get here.
  const captcha = useCapCaptcha(captchaCfg ?? undefined);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const captchaToken = captchaCfg?.enabled ? await captcha.getToken() : undefined;
      if (captchaCfg?.enabled && !captchaToken) {
        throw new Error('Captcha verification failed. Please try again.');
      }
      const res = await twoFactorLogin({
        twoFactorToken: challenge.twoFactorToken,
        code: mode === 'totp' ? code.trim() : undefined,
        recoveryCode: mode === 'recovery' ? code.trim() : undefined,
        captchaToken: captchaToken ?? undefined,
      });
      onSuccess(res.user);
    } catch (err) {
      if (captchaCfg?.enabled) captcha.reset();
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center px-6">
      <Card className="w-full max-w-md">
        <h1 className="text-xl font-semibold">Two-factor authentication</h1>
        <p className="text-sm text-slate-500 mt-1">
          {mode === 'totp'
            ? 'Enter the 6-digit code from your authenticator app.'
            : 'Enter one of your one-time recovery codes.'}
        </p>
        <form onSubmit={submit} className="flex flex-col gap-4 mt-5">
          <Input
            label={mode === 'totp' ? 'Authenticator code' : 'Recovery code'}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode={mode === 'totp' ? 'numeric' : 'text'}
            autoComplete="one-time-code"
            autoFocus
            required
          />
          {error ? <div className="text-sm text-red-600">{error}</div> : null}
          <Button type="submit" loading={submitting}>
            Verify
          </Button>
          {captchaCfg?.enabled ? <CaptchaStatusBadge state={captcha.state} /> : null}
          <button
            type="button"
            className="text-sm text-slate-600 hover:underline self-center"
            onClick={() => {
              setMode((m) => (m === 'totp' ? 'recovery' : 'totp'));
              setCode('');
              setError(null);
            }}
          >
            {mode === 'totp'
              ? "I don't have access to my authenticator — use a recovery code"
              : 'Use my authenticator app instead'}
          </button>
          <button
            type="button"
            className="text-xs text-slate-500 hover:underline self-center"
            onClick={onCancel}
          >
            Cancel and start over
          </button>
        </form>
      </Card>
    </div>
  );
}
